import {FunctionInfo} from '@mintdata-wsp/common/controllers/network/generated/requests/FunctionInfo';
import {ImportFunctionsRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/ImportFunctionsRequest';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {
    CopyPagesDesignState,
    CopyPagesState,
    ListTypes,
    PageInfo,
    ProjectInfo,
    ResetStateType,
    SheetInfo,
    VersionInfo
} from '@mintdata-wsp/common/state/ui/visualDesigner/CopyPagesState';
import {IDesignStateSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {
    IMeshCellNoteSnapshot,
    MeshCellNote
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellNote';
import {
    IMeshCellSettingsSnapshot,
    MeshCellSettings
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {IMeshStateSnapshot, MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {INamedRangeSnapshot, NamedRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/NamedRange';
import {ISheetSnapshot, Sheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {
    ISheetSettingsSnapshot,
    SheetSettings
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetSettings';
import {AnySheet, IAnySheetSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SlotSnapshotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {
    EventBinding,
    IBindingSnapshot,
    IEventBindingSnapshot,
    IPropBindingSnapshot,
    PropBinding
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/binding/Binding';
import {BoxTypes, IBoxSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {
    IEventPropsSnapshot,
    IPropsSnapshot
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {IPageSnapshot, Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {IPropSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {SystemPropsConfigs} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/SystemPropsConfigs';
import {WindowLocationUtil} from '@mintdata-wsp/common/util/WindowLocationUtil';
import {CopyPagesRegExp} from '@mintdata-wsp/wsp/controllers/visualDesigner/designState/CopyPagesRegExp';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {paths} from '@mintdata-wsp/wsp/ui/routes/Routing';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {ArrayUtils} from '@mintdata/mint-kit/impl/util/ArrayUtils';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import {action, runInAction} from 'mobx';
import _flatMap from 'lodash/flatMap';
import _mapValues from 'lodash/mapValues';
import _uniq from 'lodash/uniq';

export type SheetRelatedThings = {
    sheets:Array<ISheetSnapshot>,
    namedRanges:Array<INamedRangeSnapshot>,
    meshStates:Array<IMeshStateSnapshot>,
    sheetSettings:Array<ISheetSettingsSnapshot>,
    cellSettings:Array<IMeshCellSettingsSnapshot>,
    cellNotes:Array<IMeshCellNoteSnapshot>,
    usedFunctions:Array<string>
}

type FlowInfo = {
    id:number,
    name:string
}

type ObjOp<T> = (o:T) => void;
type ObjMap<T> = {
    [key:string]:T;
}

const CURRENT_VERSION:VersionInfo = {
    id: -1,
    name: 'Current Version'
};

export class CopyPagesController
{
    public getState():CopyPagesState
    {
        const designState = controllers.ui.openDesign.getDesignState();
        return designState && designState.surface ? designState.surface.uiState.copyPages : null;
    }

    @action
    public toggleCopyRelatedSheets()
    {
        const state = this.getState();
        state.designState.isCopyRelatedSheets = !state.designState.isCopyRelatedSheets;
    }

    public isCopyRelatedSheets():boolean
    {
        const state = this.getState();
        return state && state.designState.isCopyRelatedSheets;
    }

    public isCopyInProgress():boolean
    {
        const state = this.getState();
        return state && state.isCopyingInProgress;
    }

    public getSelectedDesignName():string
    {
        const state = this.getState();
        return state && state.selectedDesign && state.selectedDesign.name;
    }

    public generateProjectAndDesignName(projectName:string, designName:string):string
    {
        if( projectName && designName )
            return `${projectName}  →  ${designName}`;

        return '';
    }

    @action.bound
    public async refreshState()
    {
        const state = this.getState();
        if( !state )
            return;

        const {context} = controllers;

        state.reset(ResetStateType.All);

        state.shouldShowLoader = true;
        state.selectedOrganizationSid = context.currentOrganizationSid();
        state.selectedOrganizationName = context.currentOrganizationName();

        this.loadOrganizationsAsync();
    }

    @action
    public async loadOrganizationsAsync(fromUI:boolean = false)
    {
        const state = this.getState();
        const {organizations: organizationsCtrl} = controllers.data;
        const {context} = controllers;

        state.organizationsLoading = true;
        state.organizations = [];

        const organizations = await Promise
            .all([
                organizationsCtrl.getAllForCurrentUser(),
                organizationsCtrl.getPublicOrganizations()
            ])
            .then(([userOrganizations, publicOrganizations]) =>
                [...publicOrganizations, ...userOrganizations]
            );

        let selectedOrganizationSid = state.selectedOrganizationSid;
        let selectedOrganizationName = state.selectedOrganizationName;

        if( !organizations.find(org => org.siteId === selectedOrganizationSid) )
        {
            selectedOrganizationSid = context.currentOrganizationSid();
            selectedOrganizationName = context.currentOrganizationName();
        }

        runInAction(() =>
        {
            state.organizations = organizations;
            state.selectedOrganizationSid = selectedOrganizationSid;
            state.selectedOrganizationName = selectedOrganizationName;
            state.organizationsLoading = false;
        });

        !fromUI && this.onOrganizationSelected(selectedOrganizationSid, selectedOrganizationName);
    }

    @action
    public async onOrganizationSelected(organizationSid:string, name:string)
    {
        const state = this.getState();
        state.reset(ResetStateType.OrganizationChange);

        if( !state.shouldShowLoader )
            state.shouldShowLoader = true;

        state.selectedOrganizationSid = organizationSid;
        state.selectedOrganizationName = name;

        this.loadDesignsAsync();
    }

    @action
    public async loadDesignsAsync(fromUI:boolean = false)
    {
        const {projects: projectsCtrl} = controllers.data;

        const state = this.getState();
        state.designsLoading = true;
        state.projects = [];

        const thisPromise = projectsCtrl.getOrganizationProjects(state.selectedOrganizationSid);
        state.designsLoadingPromise = thisPromise;

        const projects = await thisPromise;

        // ignore stale data if new promise is in da house
        if( state.designsLoadingPromise !== thisPromise )
            return;

        let projectsToState:Array<ProjectInfo> = [];
        let selectedProjectId = state.designState.selectedProjectId;
        let selectedDesignId = state.designState.selectedDesignId;
        let selectedProjectAndDesignName = state.selectedProjectAndDesignName;
        let hasSelectedDesignId:boolean = false;

        projects.forEach(project =>
        {
            let designs = [];
            project.designs.forEach(design =>
            {
                if( design.id === selectedDesignId )
                {
                    selectedProjectId = project.id;
                    hasSelectedDesignId = true;
                }

                designs.unshift({
                    id: design.id,
                    name: design.name,
                    projectId: project.id,
                    projectName: project.name
                });
            });

            designs.length > 0 && projectsToState.unshift({
                id: project.id,
                name: project.name,
                designs
            });
        });

        if( !hasSelectedDesignId )
        {
            if( projectsToState.length > 0 && projectsToState[0].designs.length > 0 )
            {
                selectedDesignId = projectsToState[0].designs[0].id;
                selectedProjectAndDesignName = this.generateProjectAndDesignName(projectsToState[0].name, projectsToState[0].designs[0].name);
            }
            else
            {
                selectedDesignId = null;
                selectedProjectAndDesignName = null;
            }
        }

        runInAction(() =>
        {
            state.projects = projectsToState;
            state.designState.selectedProjectId = selectedProjectId;
            state.designState.selectedDesignId = selectedDesignId;
            state.selectedProjectAndDesignName = selectedProjectAndDesignName;
            state.designsLoading = false;
        });

        !fromUI && this.onDesignSelected(selectedDesignId, selectedProjectAndDesignName);
    }

    @action
    public async onDesignSelected(designId:number, selectedProjectAndDesignName:string)
    {
        const state = this.getState();

        state.reset(ResetStateType.SpaceDesignChange);
        if( !state.shouldShowLoader )
            state.shouldShowLoader = true;

        state.designState.selectedDesignId = designId;
        state.selectedProjectAndDesignName = selectedProjectAndDesignName;

        if( state.designState.selectedDesignId )
        {
            state.versions = [CURRENT_VERSION];
            this.loadVersionsAsync();

            await this.onVersionSelected(-1);
        }
        else if( state.shouldShowLoader )
            state.shouldShowLoader = false;
    }

    @action
    public async copyPagesFromDesign(projectId:number,
                                     designId:number,
                                     versionId:number,
                                     pageNames:Array<string>,
                                     copyNamePrefix:string,
                                     isCopyRelatedSheets:boolean)
    {
        const designState:CopyPagesDesignState = new CopyPagesDesignState();

        designState.selectedProjectId = projectId;
        designState.selectedDesignId = designId;
        designState.selectedVersionId = versionId;

        await this.loadSelectedVersion(designState, projectId, designId, versionId);

        runInAction(() =>
        {
            designState.copyNamePrefix = copyNamePrefix;
            designState.isCopyRelatedSheets = isCopyRelatedSheets;

            const pageToCopy:PageInfo = designState.pages.find(
                (page:PageInfo) => pageNames.includes(page.name)
            );

            if( pageToCopy )
                pageToCopy.selected = true;
        });

        return this.copySelectedItems(designState, ListTypes.Pages);
    }

    @action
    public async loadVersionsAsync()
    {
        const state = this.getState();

        state.versionsLoading = true;
        state.versions = [];

        const {id: designId, projectId} = state.selectedDesign;

        const thisPromise = API.designSnapshots
            .list(projectId, designId)
            .then(response =>
                response.designSnapshots.map(({id, name, description}) => ({id, name, description} as VersionInfo))
            );
        state.versionsLoadingPromise = thisPromise;

        const versions = await thisPromise;

        // ignore stale data if new promise is in da house
        if( state.versionsLoadingPromise !== thisPromise )
            return;

        runInAction(() =>
        {
            if( state.designState.selectedVersionId
                && !versions.find(
                    (version:VersionInfo) => version.id === state.designState.selectedVersionId
                ) )
                state.designState.selectedVersionId = CURRENT_VERSION.id;

            state.versions = [CURRENT_VERSION, ...versions.reverse()];
            state.versionsLoading = false;
        });
    };

    @action
    public async onVersionSelected(versionId:number)
    {
        const state = this.getState();
        state.reset(ResetStateType.VersionChange);

        state.designState.selectedVersionId = versionId;
        state.pagesAndSheetsLoading = true;

        if( !state.shouldShowLoader )
            state.shouldShowLoader = true;

        const {id: designId, name: designName, projectId} = state.selectedDesign;

        await this.loadSelectedVersion(state.designState, projectId, designId, versionId);

        runInAction(() =>
        {
            state.pagesAndSheetsLoading = false;
            state.shouldShowLoader = false;

            if( !state.isCopyNamePrefixDirty )
            {
                // abbreviate design name for default name prefix
                state.designState.copyNamePrefix = designName.replace(/[^\w]*(\w)\w*[^\w]*/g, '$1') + '_';
            }
        });
    }

    @action
    public async loadSelectedVersion(designState:CopyPagesDesignState,
                                     projectId:number,
                                     designId:number,
                                     versionId:number)
    {
        //versionId === -1 for `current version`
        const {migration} = controllers.ui;
        let designStateSnapshot = null;

        if( versionId === -1 )
            designStateSnapshot = await this.loadDesign(projectId, designId);
        else
        {
            const response = await API.designSnapshots.get(projectId, designId, versionId);
            const designJson = JSON.parse(response.designerState);

            designStateSnapshot = designJson.visualState ? designJson.visualState : designJson;
        }

        migration.migrateDesign(designStateSnapshot);

        const pages = designStateSnapshot.surface.pagesItemsOrdered
            .map(pageId => designStateSnapshot.surface.pages[pageId])
            .map(({id, name, overlays}) =>
                ({
                    id,
                    name,
                    selected: false,
                    dependencies: overlays.map(o => o.page)
                })
            );

        const sheets = Object.values(
            designStateSnapshot.spreadsheets.workbooks[designStateSnapshot.spreadsheets.rootWorkbookId].runtime.sheets
        )
            .map(({id, name}:IAnySheetSnapshot) => ({
                id,
                name,
                selected: false
            }));

        runInAction(() =>
        {
            designState.selectedDesignSnapshot = designStateSnapshot;
            designState.pages = pages;
            designState.sheets = sheets;
        });
    }

    private async loadDesign(projectId:number, designId:number)
    {
        const {designState} = await API.designs.getState(projectId, designId);

        let designStateSnapshot = JSON.parse(designState);

        return designStateSnapshot.visualState || designStateSnapshot;
    }

    @action
    public togglePageSelection(page:PageInfo)
    {
        page.selected = !page.selected;
    }

    @action
    public toggleSheetSelection(sheet:SheetInfo)
    {
        sheet.selected = !sheet.selected;
    }

    @action
    public setCopyNamePrefix(prefix:string)
    {
        const state = this.getState();
        state.designState.copyNamePrefix = prefix;
        state.isCopyNamePrefixDirty = true;
    }

    @action
    public setDesignsSearchString(value:string)
    {
        const state = this.getState();
        state.designsSearchString = value;
    }

    @action
    public setCurrentListType(type:ListTypes)
    {
        const state = this.getState();
        state.currentListType = type;
    }

    public getLinkForSelectedDesign():string
    {
        const {selectedOrganizationSid, selectedDesign}:CopyPagesState = this.getState();

        if( !selectedOrganizationSid || !selectedDesign )
            return null;

        const relPath:string = paths.design(selectedDesign.projectId, selectedDesign.id);

        return `${window.location.protocol}//${WindowLocationUtil.getLocationToFullRedirectToSiteId(selectedOrganizationSid, relPath)}`;
    }

    @action
    public async onCopySelectedItems():Promise<void>
    {
        const {undoRedo} = controllers.ui;
        const state = this.getState();

        const {
            designState,
            currentListType
        } = state;

        state.isCopyingInProgress = true;

        await this.copySelectedItems(designState, currentListType);

        runInAction(() =>
        {
            undoRedo.clear();
            state.resetSelectedItems();
            state.isCopyingInProgress = false;
        });
    }

    @action
    public async copySelectedItems(designState:CopyPagesDesignState, currentListType:ListTypes):Promise<void>
    {
        const {
            copyNamePrefix,
            isCopyRelatedSheets,
            selectedDesignSnapshot,
            pages: pageInfos,
            sheets: sheetInfos
        } = designState;
        Assert.yes(selectedDesignSnapshot, 'design snapshot should have already been selected on that point');

        let copyOfSelectedDesignSnapshot:IDesignStateSnapshot = JSON.parse(JSON.stringify(selectedDesignSnapshot));

        const pages:Array<IPageSnapshot> = currentListType === ListTypes.Pages
            ? this._collectPages(pageInfos, copyOfSelectedDesignSnapshot)
            : [];
        const bindings:Array<IBindingSnapshot> = currentListType === ListTypes.Pages && isCopyRelatedSheets
            ? this._collectBindings(copyOfSelectedDesignSnapshot, pages)
            : [];

        let sheets:Array<ISheetSnapshot> = [];

        if( currentListType === ListTypes.Pages )
        {
            sheets = this._collectSheetsByBindings(bindings, copyOfSelectedDesignSnapshot);
        }
        else
        {
            const selectedSheetIds:Array<string> = sheetInfos.reduce((acc:string[], sheet:SheetInfo) =>
            {
                sheet.selected && acc.push(sheet.id);
                return acc;
            }, []);

            sheets = this._collectSheetsByIds(selectedSheetIds, copyOfSelectedDesignSnapshot);
        }

        const {sheets: allSheets, usedFunctions} = this._collectRelatedSheetsAndFunctionNames(sheets, copyOfSelectedDesignSnapshot);
        const sheetThings = this._collectSheetsRelatedThings(allSheets, usedFunctions, copyOfSelectedDesignSnapshot, bindings);

        const flows = await this._collectFlows(designState, sheetThings);

        this._remapObjects(pages, bindings, sheetThings, flows, copyNamePrefix);

        await this._importFlows(designState, flows);

        this._importObjects(pages, bindings, sheetThings);

        copyOfSelectedDesignSnapshot = null;
    }

    private _collectPages(pageInfos:Array<PageInfo>, selectedDesignSnapshot:IDesignStateSnapshot)
    {
        const selectedIdMap = ArrayUtils.arrayToFlatMap(
            pageInfos.filter(page => page.selected),
            page => [page.id, ...page.dependencies]
        );

        return Object
            .keys(selectedIdMap)
            .map(pageId => selectedDesignSnapshot.surface.pages[pageId]);
    }

    private async _collectFlows(designState:CopyPagesDesignState, sheetThings:SheetRelatedThings):Promise<Array<FlowInfo>>
    {
        const {selectedDesignId, selectedProjectId, selectedVersionId} = designState;

        let functionsResponse;

        if( selectedVersionId === -1 )
            functionsResponse = await API.functions.list(selectedDesignId);
        else
            functionsResponse = await API.designSnapshots.listFunctions(
                selectedProjectId,
                selectedDesignId,
                selectedVersionId
            );

        const usedFunctionsMap = ArrayUtils.arrayToMap(sheetThings.usedFunctions, name => name);

        return functionsResponse.functions
            .filter(f => usedFunctionsMap[f.name])
            .map(({id, name}) => ({id, name} as FlowInfo));
    }

    private _collectSheetsRelatedThings(sheets:Array<ISheetSnapshot>,
                                        usedFunctions:Array<string>,
                                        selectedDesignSnapshot:IDesignStateSnapshot,
                                        bindings:Array<IBindingSnapshot>):SheetRelatedThings
    {
        const rootWorkbookId = selectedDesignSnapshot.spreadsheets.rootWorkbookId;
        const workbook = selectedDesignSnapshot.spreadsheets.workbooks[rootWorkbookId];
        const viewState = workbook.view;
        const runtimeState = workbook.runtime;

        const rangeIds = _uniq(bindings.map(binding => binding.rangeId));
        const namedRanges = rangeIds.map(rangeId => runtimeState.namedRanges[rangeId]).filter(range => !!range);

        const meshStates = sheets.map(sheet => viewState.meshStates[sheet.id]);

        const sheetSettings = Object.values(viewState.sheetSettings)
            .filter(sheetSetting => sheets.find(sheet => sheet.id === sheetSetting.sheetId));

        const cellSettingsIds = _flatMap(sheetSettings, sheetSettings => Object.values(sheetSettings.cellSettings));
        const cellNotesIds = _flatMap(sheetSettings, sheetSettings => Object.values(sheetSettings.cellNotes));

        const cellSettings = cellSettingsIds.map(settingId => viewState.cellSettings[settingId])
            .filter(setting => !!setting);
        const cellNotes = cellNotesIds.map(noteId => viewState.cellNotes[noteId]).filter(note => !!note);

        return {sheets, namedRanges, meshStates, sheetSettings, cellSettings, cellNotes, usedFunctions};
    }

    private _collectSheetsByBindings(bindings:Array<IBindingSnapshot>, selectedDesignSnapshot:IDesignStateSnapshot):Array<ISheetSnapshot>
    {
        const rootWorkbookId = selectedDesignSnapshot.spreadsheets.rootWorkbookId;
        const workbook = selectedDesignSnapshot.spreadsheets.workbooks[rootWorkbookId];
        const runtimeState = workbook.runtime;

        // only client sheets are handled for now
        const clientSheets = runtimeState.sheets
            .filter(sheet => (sheet as any).slots)
            .map(sheet => sheet as ISheetSnapshot);

        const sheetById = ArrayUtils.keyMap(clientSheets, sheet => sheet.id);
        const addedSheetByName = {};

        const sheets:Array<ISheetSnapshot> = [];
        bindings.forEach(binding =>
        {
            const sheet = sheetById[binding.sheetId] as ISheetSnapshot;
            if( !addedSheetByName[sheet.name] )
            {
                sheets.push(sheet);
                addedSheetByName[sheet.name] = sheet;
            }
        });

        return sheets;
    }

    private _collectSheetsByIds(sheetsIds:Array<string>, selectedDesignSnapshot:IDesignStateSnapshot):Array<ISheetSnapshot>
    {
        const rootWorkbookId = selectedDesignSnapshot.spreadsheets.rootWorkbookId;
        const workbook = selectedDesignSnapshot.spreadsheets.workbooks[rootWorkbookId];
        const runtimeState = workbook.runtime;

        return runtimeState.sheets
            .filter(sheet => (sheet as any).slots)
            .filter(sheet => sheetsIds.includes(sheet.id))
            .map(sheet => sheet as ISheetSnapshot);
    }

    private _collectRelatedSheetsAndFunctionNames(sheets:Array<ISheetSnapshot>, selectedDesignSnapshot:IDesignStateSnapshot)
    {
        const rootWorkbookId = selectedDesignSnapshot.spreadsheets.rootWorkbookId;
        const workbook = selectedDesignSnapshot.spreadsheets.workbooks[rootWorkbookId];
        const runtimeState = workbook.runtime;

        // only client sheets are handled for now
        const clientSheets = runtimeState.sheets
            .filter(sheet => (sheet as any).slots)
            .map(sheet => sheet as ISheetSnapshot);

        const sheetByName = ArrayUtils.keyMap(clientSheets, sheet => sheet.name);
        let addedSheetByName = sheets.reduce((acc:Object, sheet:IAnySheetSnapshot) =>
        {
            acc[sheet.name] = sheet;
            return acc;
        }, {});

        // add external sheets found in formulas... + collect used functions names
        const usedFunctionsMap = {};
        for( let i = 0; i < sheets.length; ++i )
        {
            const sheet = sheets[i];
            Object.values(sheet.slots).forEach(slot =>
            {
                this._collectFunctionNamesInFormula(slot.formula)
                    .forEach(functionName => usedFunctionsMap[functionName] = true);

                this._collectSheetNamesInFormula(slot.formula)
                    .forEach(sheetName =>
                    {
                        if( addedSheetByName[sheetName] )
                            return;

                        const sheet = sheetByName[sheetName];
                        if( sheet )
                        {
                            sheets.push(sheet);
                            addedSheetByName[sheetName] = sheet;
                        }
                    });
            });
        }

        return {sheets, usedFunctions: Object.keys(usedFunctionsMap)};
    }

    private _collectBindings(selectedDesignSnapshot:IDesignStateSnapshot, pages:Array<IPageSnapshot>):Array<IBindingSnapshot>
    {
        const bindingIds = [];
        const collectBindingsFromPropsFn = (props:IPropsSnapshot) =>
            Object.values(props).forEach(prop => bindingIds.push(...prop.bindingIds.filter(bindingId => !!bindingId)));
        const collectBindingsFromEventPropsFn = (eventProps:IEventPropsSnapshot) =>
            Object.values(eventProps).forEach(eventProp => eventProp.bindingId && bindingIds.push(eventProp.bindingId));

        pages.forEach(page =>
        {
            Object.values(page.artboard.allBoxes).forEach(box =>
            {
                collectBindingsFromPropsFn(box.componentDesc._baseProperties);
                collectBindingsFromPropsFn(box.componentDesc._layoutProperties);
                collectBindingsFromPropsFn(box.componentDesc._properties);
                collectBindingsFromEventPropsFn(box.componentDesc._baseEventProps);
                collectBindingsFromEventPropsFn(box.componentDesc._eventProps);
            });
        });

        const bindingsMap = selectedDesignSnapshot.bindings;
        return bindingIds.map(bindingId => bindingsMap[bindingId]).filter(binding => !!binding);
    }

    private _remapObjects(pages:Array<IPageSnapshot>, bindings:Array<IBindingSnapshot>, sheetsThings:SheetRelatedThings, flows:Array<any>, copyNamePrefix:string)
    {
        const {surfaceArea, flow, spreadsheets: {main}} = controllers.ui;

        const pageNamesMap = ArrayUtils.arrayToMap(Array.from(surfaceArea.getState()
            .pages
            .values()), page => page.name);
        const sheetNamesMap = ArrayUtils.arrayToMap(main.getRootWorkbook().runtime.sheets, sheet => sheet.name);
        const flowNamesMap = ArrayUtils.arrayToMap(flow.getCloudFunctions(), cf => cf.spec.alias);

        const {sheets, namedRanges, meshStates, sheetSettings, cellSettings, cellNotes} = sheetsThings;

        const idMap = this._generateIdMap(pages, bindings, sheetsThings);

        // unfortunately traversing ful object graph is slow, therefore using less maintainable but faster point-and-shoot-approach
        const remapObjectFn = <T>(object:any, op?:ObjOp<T>) =>
        {
            if( object.id )
                object.id = (idMap[object.id] || object.id);

            if( object.sheetId )
                object.sheetId = (idMap[object.sheetId] || object.sheetId);

            if( object.pageId )
                object.pageId = (idMap[object.pageId] || object.pageId);

            if( object.rangeId )
                object.rangeId = (idMap[object.rangeId] || object.rangeId);

            if( object.slotId )
                object.slotId = (idMap[object.slotId] || object.slotId);

            if( op )
                op(object as T);
        };

        const remapArrayFn = <T>(array:Array<T>, op?:ObjOp<T>) =>
        {
            array.forEach(obj => remapObjectFn(obj, op));
        };

        const remapMapFn = <T>(map:ObjMap<T>, op?:ObjOp<T>):ObjMap<T> =>
        {
            const newMap = {};
            Object.values(map).forEach(obj =>
            {
                remapObjectFn(obj, op);
                newMap[(obj as any).id] = obj;
            });
            return newMap;
        };

        const remapMapValuesFn = (map:ObjMap<string>):ObjMap<string> =>
            _mapValues(map, v => idMap[v] || v);

        const remapPropsFn = (props:IPropsSnapshot) => Object.values(props)
            .forEach(prop =>
                prop.bindingIds && prop.bindingIds.forEach((bid, i) => prop.bindingIds[i] = idMap[bid])
            );

        const remapEventPropsFn = (eventProps:IEventPropsSnapshot) => Object.values(eventProps)
            .forEach(eventProp => eventProp.bindingId = eventProp.bindingId && idMap[eventProp.bindingId]);

        const remapNameFn = (baseName:string, namesMap:any, newNameFn?:(baseName:string, counter:number) => string):string =>
        {
            baseName = copyNamePrefix + baseName;

            // `baseName (11)` is default format for conflict names resolution
            newNameFn = newNameFn || ((baseName, counter) => `${baseName} (${counter})`);

            let name = baseName;
            let counter = 2;
            while( namesMap[name] )
                name = newNameFn(baseName, counter++);

            return name;
        };

        const remapNamesFn = (objects:Array<any>, namesMap:any, newNameFn?:(baseName:string, counter:number) => string) =>
        {
            const renamingsMap = {};
            objects.forEach(object =>
            {
                const oldName = object.name;
                const newName = remapNameFn(object.name, namesMap, newNameFn);
                if( oldName !== newName )
                {
                    object.name = newName;
                    renamingsMap[oldName] = newName;
                }
            });
            return renamingsMap;
        };

        const sheetRenamings = remapNamesFn(sheets, sheetNamesMap);
        const flowRenamings = remapNamesFn(flows, flowNamesMap, (baseName, counter) => `${baseName}_${counter}`);

        remapArrayFn(pages, page =>
        {
            page.sectionName = 'Copied Pages';
            page.name = remapNameFn(page.name, pageNamesMap);
            page.overlays.map(overlay => overlay.page = idMap[overlay.page]);

            Object.values(page.artboard.allBoxes).forEach(box =>
            {
                remapPropsFn(box.componentDesc._baseProperties);
                remapPropsFn(box.componentDesc._layoutProperties);
                remapPropsFn(box.componentDesc._properties);
                remapEventPropsFn(box.componentDesc._baseEventProps);
                remapEventPropsFn(box.componentDesc._eventProps);
            });
        });

        remapArrayFn(sheets, sheet =>
        {
            sheet.slotLocationToSlot = remapMapValuesFn(sheet.slotLocationToSlot);
            sheet.slots = remapMapFn(sheet.slots, slot =>
            {
                slot.formula = this._changeSheetNamesInFormula(slot.formula, sheetRenamings);
                slot.formula = this._changeFunctionNamesInFormula(slot.formula, flowRenamings);
            });
        });

        remapArrayFn(bindings);
        remapArrayFn(namedRanges);
        remapArrayFn(meshStates);

        remapArrayFn(sheetSettings, sheetSetting =>
        {
            sheetSetting.cellSettings = remapMapValuesFn(sheetSetting.cellSettings);
            sheetSetting.cellNotes = remapMapValuesFn(sheetSetting.cellSettings);
        });

        remapArrayFn(cellSettings);
        remapArrayFn(cellNotes);
    }

    private _generateIdMap(pages:Array<IPageSnapshot>, bindings:Array<IBindingSnapshot>, sheetsThings:SheetRelatedThings)
    {
        const {sheets, namedRanges, sheetSettings, cellSettings, cellNotes} = sheetsThings;

        const idMap = {};
        const generateRemapIdsFn = (objects:Array<any>) => objects.forEach(obj =>
        {
            let newId = IdUtil.newGUID();
            let id = obj.id as string;

            // handle ids for sheetSettings' like: "c83b9cbc-5f3e-4584-93d9-45b0bcc410d9_'column1'[1]"
            // where the actual id should be equal to corresponding slotId
            if( id.length > newId.length )
            {
                const baseId = id.substring(0, newId.length);
                const suffixId = id.substring(newId.length);

                idMap[baseId] = newId;
                newId += suffixId;
            }

            idMap[id] = newId;
        });

        generateRemapIdsFn(pages);
        generateRemapIdsFn(bindings);
        generateRemapIdsFn(sheets);
        generateRemapIdsFn(namedRanges);
        generateRemapIdsFn(sheetSettings);
        generateRemapIdsFn(cellSettings);
        generateRemapIdsFn(cellNotes);

        return idMap;
    }

    @bind
    private async _importFlows(designState:CopyPagesDesignState, flows:Array<FlowInfo>)
    {
        if( flows.length === 0 )
            return;

        const {openDesign, flow} = controllers.ui;
        const {projectId: currentProjectId, designId: currentDesignId, version: designVersion} = openDesign.getOpenDesign().designRef;

        const {selectedDesignId, selectedVersionId} = designState;

        const functions = flows.map(({id, name}) => new FunctionInfo(id, name));
        const importRequest = new ImportFunctionsRequest(selectedDesignId, selectedVersionId, functions, designVersion);

        await API.functions.importFunctions(currentDesignId, importRequest);
        await flow.refreshFlows(currentProjectId, currentDesignId);
    }

    @action.bound
    private _importObjects(pages:Array<IPageSnapshot>, bindings:Array<IBindingSnapshot>, sheetsThings:SheetRelatedThings)
    {
        const {surfaceArea} = controllers.ui;
        const {main, workbook: workbookCtrl} = controllers.ui.spreadsheets;

        const workbook = main.getRootWorkbook();
        const viewState = workbook.view;
        const runtimeState = workbook.runtime;

        const designState = controllers.ui.openDesign.getDesignState();
        const bindingState = designState.binding.runtime;
        const spreadsheetsState = designState.spreadsheets;
        const surfaceState = designState.surface;

        const {sheets, namedRanges, meshStates, sheetSettings, cellSettings, cellNotes} = sheetsThings;

        sheetSettings.forEach(sheetSettingSnapshot =>
            viewState.addSheetSettings(SheetSettings.fromJsonDeep(sheetSettingSnapshot))
        );

        cellSettings.forEach(cellSettingsSnapshot =>
            viewState.addCellSettings(MeshCellSettings.fromJsonDeep(cellSettingsSnapshot))
        );

        cellNotes.forEach(cellNotesSnapshot =>
            viewState.addCellNote(MeshCellNote.fromJsonDeep(cellNotesSnapshot))
        );

        const meshStateMap = ArrayUtils.keyMap(meshStates, s => s.sheetId);
        const sheetsMap = new Map<SheetId, AnySheet>();

        sheets.forEach(sheetSnapshot =>
        {
            const sheet = Sheet.fromJsonDeep(sheetSnapshot, workbook);
            sheetsMap.set(sheet.id, sheet);

            const meshState = MeshState.fromJsonDeep(meshStateMap[sheet.id], sheetsMap);
            const sheetSettings = MeshState.fromJsonDeep(meshStateMap[sheet.id], sheetsMap);

            runtimeState.insertSheet(runtimeState.sheets.length, sheet);
            viewState.addMeshState(meshState);

            workbookCtrl.initializeSheet(sheet);
        });

        namedRanges.forEach(rangeSnapshot =>
            runtimeState.addNamedRange(NamedRange.fromJsonDeep(rangeSnapshot))
        );

        const pagesMap = {};
        pages.forEach(pageSnapshot =>
        {
            pageSnapshot = this._setViewportSize(pageSnapshot);
            const page = Page.fromJsonDeep(pageSnapshot);
            pagesMap[page.id] = page;
            surfaceState.addPage(page);
        });

        Object.values(pagesMap).forEach((page:Page) =>
            page.overlays.forEach(overlay => overlay.page = pagesMap[overlay.page.id])
        );

        bindings.forEach(bindingSnapshot =>
            {
                const binding = bindingSnapshot.isPropBinding
                    ? PropBinding.fromJsonDeep(bindingSnapshot as IPropBindingSnapshot, spreadsheetsState, surfaceState)
                    : EventBinding.fromJsonDeep(bindingSnapshot as IEventBindingSnapshot, spreadsheetsState, surfaceState);

                if( binding )
                    bindingState.addBinding(binding);
                else
                    console.warn('WARNING!!! Invalid Binding has been skipped');
            }
        );
    }

    private _setViewportSize(pageSnapshot:IPageSnapshot):IPageSnapshot
    {
        const designState = controllers.ui.openDesign.getDesignState();
        const surfaceState = designState.surface;
        const viewport = surfaceState.activePage.artboard.viewport;
        const currentViewportComponentDesc = viewport.componentDesc;

        Object.values(pageSnapshot.artboard.allBoxes).forEach((boxSnapshot:IBoxSnapshot) =>
        {
            if( boxSnapshot.boxType === BoxTypes.Viewport )
            {
                const {componentDesc: {_layoutProperties}} = boxSnapshot;

                Object.values(_layoutProperties).forEach((layoutProp:IPropSnapshot) =>
                {
                    let propName:string = null;

                    switch( layoutProp.name )
                    {
                        case SystemPropsConfigs.LAYOUT_WIDTH_PROP_NAME:
                            propName = 'layoutWidth';
                            break;
                        case SystemPropsConfigs.LAYOUT_HEIGHT_PROP_NAME:
                            propName = 'layoutHeight';
                            break;
                        case SystemPropsConfigs.LAYOUT_TOP_PROP_NAME:
                            propName = 'layoutTop';
                            break;
                        case SystemPropsConfigs.LAYOUT_LEFT_PROP_NAME:
                            propName = 'layoutLeft';
                            break;
                    }

                    if( propName )
                        layoutProp._value.value.value = currentViewportComponentDesc[propName].value.value;
                });
            }
        });

        return pageSnapshot;
    }

    private _collectSheetNamesInFormula(formula:SlotSnapshotFormula):Array<string>
    {
        return this._collectByRegexpInFormula(formula, CopyPagesRegExp.FORMULA_SHEET_REFS_RE(), (match, collection) =>
        {
            if( match[1] || match[2] )
            {
                // unescape single-quote
                collection.push(
                    match[2] || match[1].replace('\'\'', '\'')
                );
            }
        });
    }

    private _collectFunctionNamesInFormula(formula:SlotSnapshotFormula):Array<string>
    {
        return this._collectByRegexpInFormula(formula, CopyPagesRegExp.FORMULA_FUNCTION_REFS_RE(), (match, collection) =>
        {
            if( match[1] )
            {
                collection.push(match[1]);
            }
        });
    }

    private _changeSheetNamesInFormula(formula:SlotSnapshotFormula, sheetRenamings:any):SlotSnapshotFormula
    {
        return this._changeByRegexpInFormula(formula, CopyPagesRegExp.FORMULA_SHEET_REFS_RE(), (match, g1, g2) =>
        {
            if( !g1 && !g2 ) return match;

            // unescape single-quote
            const oldName = g2 || g1.replace('\'\'', '\'');
            const newName = sheetRenamings[oldName];
            if( !newName ) return match;

            // escape single-quote
            return `'${newName.replace('\'', '\'\'')}'!`;
        });
    }

    private _changeFunctionNamesInFormula(formula:SlotSnapshotFormula, functionRenamings:any):SlotSnapshotFormula
    {
        return this._changeByRegexpInFormula(formula, CopyPagesRegExp.FORMULA_FUNCTION_REFS_RE(), (match, g1) =>
        {
            if( !g1 ) return match;

            const newName = functionRenamings[g1];
            return newName ? newName + '(' : match;
        });
    }

    private _collectByRegexpInFormula<T>(formula:SlotSnapshotFormula, re:RegExp, matchHandler:(match:any, collection:Array<T>) => void):Array<T>
    {
        if( typeof formula !== 'string' || !(formula.startsWith('=') || formula.startsWith('+=')) )
            return [];

        const collection = [];

        let match = re.exec(formula);
        while( match )
        {
            matchHandler(match, collection);
            match = re.exec(formula);
        }

        return collection;
    }

    private _changeByRegexpInFormula(formula:SlotSnapshotFormula, re:RegExp, replaceFn:any):string
    {
        if( typeof formula !== 'string' || !(formula.startsWith('=') || formula.startsWith('+=')) )
            return formula;

        return formula.replace(re, (match, g1, g2) =>
            replaceFn(match, g1, g2)
        );
    }
}
